import {
  IdentificationCard,
  Airplane,
  Buildings,
  Car,
  Armchair,
  Star,
  Globe,
  UsersThree,
  ShieldPlus,
} from "@phosphor-icons/react";

/**
 * Single source of truth for the Travel Vault data model.
 *
 * Each category id maps to:
 *   - storage list key (CAT_TO_LIST + LIST_KEYS)
 *   - tile metadata (CATEGORIES) → label, sub, color, soft, optional iconSrc
 *   - add-form fields (FIELDS)
 *   - rendering rules (VIEWS) → both the compact summary lines and the
 *     unmasked detail rows shown in the expanded card. Keeps line layout
 *     and detail-row layout in one place per category so they can never
 *     drift apart again.
 */

export const LS_KEY = "tiplingo_vault_v1";
export const MAX_FAMILY = 3;

export const ID_TYPES = [
  { id: "passport", label: "Passport" },
  { id: "drivers_license", label: "Driver's License" },
  { id: "other", label: "Other ID" },
];

// Shared surface styling for any vault screen (grid + category sub-pages).
// Keeping it in one place ensures the steel-wall aesthetic stays consistent
// when new sub-views are added, and a single asset swap propagates to every
// surface.
export const VAULT_SURFACE_STYLE = {
  backgroundImage: "url('/assets/vault-icons/vault-steel-bg.png')",
  backgroundSize: "cover",
  backgroundPosition: "top center",
  backgroundRepeat: "no-repeat",
  backgroundAttachment: "fixed",
};

export const CATEGORIES = [
  { id: "photo_id", label: "Photo Identification", sub: "Passport, license & gov ID", Icon: IdentificationCard, iconSrc: "/assets/vault-icons/passport.png", buttonSrc: "/assets/vault-icons/btn-photoid.png", color: "#0E2C54", soft: "#EAF2FB", live: true },
  { id: "insurance", label: "Travel Insurance", sub: "You + up to 3 family members", Icon: ShieldPlus, buttonSrc: "/assets/vault-icons/btn-insurance.png", color: "#2E7D5A", soft: "#DCEFE3", live: true },
  { id: "flight", label: "Flight", sub: "Airline & confirmation", Icon: Airplane, iconSrc: "/assets/vault-icons/flight.png", buttonSrc: "/assets/vault-icons/btn-flight.png", color: "#2A6FD8", soft: "#EAF2FB", live: true },
  { id: "hotel", label: "Hotel", sub: "Stays & check-in dates", Icon: Buildings, buttonSrc: "/assets/vault-icons/btn-hotel.png", color: "#2E7D5A", soft: "#DCEFE3", live: true },
  { id: "family", label: "Family", sub: "Up to 3 travel companions", Icon: UsersThree, buttonSrc: "/assets/vault-icons/btn-family.png", color: "#E55B2C", soft: "#FDE6D8", live: true },
  { id: "car", label: "Car rental", sub: "Pickup, dropoff & confirmation", Icon: Car, iconSrc: "/assets/vault-icons/car.png", buttonSrc: "/assets/vault-icons/btn-car.png", color: "#E55B2C", soft: "#FDE6D8", live: true },
  { id: "lounge", label: "Lounge", sub: "Priority Pass & memberships", Icon: Armchair, iconSrc: "/assets/vault-icons/lounge.png", buttonSrc: "/assets/vault-icons/btn-lounge.png", color: "#0E2C54", soft: "#EAF2FB", live: true },
  { id: "tsaprecheck", label: "TSA PreCheck®", sub: "Known Traveler Number", Icon: Star, iconSrc: "/assets/vault-icons/tsa.png", buttonSrc: "/assets/vault-icons/btn-tsa.png", color: "#2E7D5A", soft: "#DCEFE3", live: true },
  { id: "globalentry", label: "Global Entry", sub: "PASS ID & expiry", Icon: Globe, iconSrc: "/assets/vault-icons/globalentry.png", buttonSrc: "/assets/vault-icons/btn-globalentry.png", color: "#2A6FD8", soft: "#EAF2FB", live: true },
];

export const CAT_TO_LIST = {
  photo_id: "photo_ids",
  insurance: "insurances",
  flight: "flights",
  hotel: "hotels",
  family: "family",
  car: "cars",
  lounge: "lounges",
  tsaprecheck: "tsa_precheck",
  globalentry: "global_entry",
};

export const LIST_KEYS = Object.values(CAT_TO_LIST);

export const EMPTY = LIST_KEYS.reduce((acc, k) => ({ ...acc, [k]: [] }), {});

export const FIELDS = {
  flight: [
    { id: "airline", label: "Airline", placeholder: "Delta" },
    { id: "flight_no", label: "Flight #", placeholder: "DL 263" },
    { id: "date", label: "Date", placeholder: "Aug 14 2026" },
    { id: "from", label: "From", placeholder: "JFK" },
    { id: "to", label: "To", placeholder: "CDG" },
    { id: "confirmation", label: "Confirmation #", placeholder: "ABCDE1" },
  ],
  // Travel Insurance — supports one entry per person (self or one of up to
  // 3 family members). The `holder` field is rendered as a select pulled
  // from the live family list at runtime (options: "holders"); see
  // AddEntryForm for the resolution logic. Keeping each policy as its own
  // entry (rather than one entry covering everyone) maps cleanly onto how
  // travel-insurance policies are actually issued and keeps the lost-
  // luggage / claim-organization flow easy to attach later.
  insurance: [
    {
      id: "holder",
      label: "Insured",
      type: "select",
      options: "holders",
      hint: "Tip: add Family members to assign a policy to them.",
    },
    { id: "provider", label: "Insurance provider", placeholder: "Allianz Travel" },
    { id: "policy_no", label: "Policy #", placeholder: "AT-3398-22K" },
    { id: "plan_type", label: "Plan type", placeholder: "Single trip · Medical + cancellation" },
    { id: "coverage_start", label: "Coverage starts", placeholder: "Aug 14 2026" },
    { id: "coverage_end", label: "Coverage ends", placeholder: "Aug 21 2026" },
    { id: "emergency_phone", label: "24/7 emergency #", placeholder: "+1 800 555 0199" },
  ],
  hotel: [
    { id: "name", label: "Hotel name", placeholder: "Hotel Lutetia" },
    { id: "checkin", label: "Check-in", placeholder: "Aug 14 2026" },
    { id: "checkout", label: "Check-out", placeholder: "Aug 21 2026" },
    { id: "confirmation", label: "Confirmation #", placeholder: "9938-2K" },
  ],
  family: [
    { id: "name", label: "Name", placeholder: "Alex Traveler" },
    { id: "relation", label: "Relation", placeholder: "Spouse, child, parent…" },
  ],
  car: [
    { id: "company", label: "Rental company", placeholder: "Hertz" },
    { id: "vehicle", label: "Vehicle class", placeholder: "Compact SUV" },
    { id: "pickup_location", label: "Pickup location", placeholder: "CDG Airport — Terminal 2E" },
    { id: "pickup_date", label: "Pickup date", placeholder: "Aug 14 2026 · 10:00" },
    { id: "dropoff_date", label: "Dropoff date", placeholder: "Aug 21 2026 · 11:30" },
    { id: "confirmation", label: "Confirmation #", placeholder: "K7Q9-44XB" },
  ],
  lounge: [
    { id: "program", label: "Program", placeholder: "Priority Pass, Amex Centurion…" },
    { id: "member_no", label: "Member #", placeholder: "9038-2113-44" },
    { id: "tier", label: "Tier (optional)", placeholder: "Prestige, Standard Plus…" },
    { id: "expires", label: "Expires", placeholder: "12 / 2027" },
  ],
  tsaprecheck: [
    { id: "holder_name", label: "Traveler name", placeholder: "As on KTN record" },
    { id: "ktn", label: "Known Traveler # (KTN)", placeholder: "12345678" },
    { id: "expires", label: "Expires", placeholder: "08 / 18 / 2030" },
  ],
  globalentry: [
    { id: "holder_name", label: "Traveler name", placeholder: "As on PASS ID" },
    { id: "pass_id", label: "PASS ID (KTN)", placeholder: "98765432" },
    { id: "expires", label: "Expires", placeholder: "08 / 18 / 2030" },
  ],
};

/* ---------- Helpers shared by lines + details ---------- */

export function maskNumber(n) {
  if (!n) return "—";
  const s = String(n);
  if (s.length <= 4) return s;
  return `••• ${s.slice(-4)}`;
}

function resolveHolder(e, family) {
  return e.holder === "self" || !e.holder
    ? "Me"
    : family?.find((f) => f.id === e.holder)?.name || "Family member";
}
function idTypeLabel(e) {
  return ID_TYPES.find((t) => t.id === e.id_type)?.label || "ID";
}

/* ---------- Per-category view rules ---------- */
/**
 * Each VIEW returns:
 *   lines(entry, family) → array of strings for the compact card header
 *     (privacy-friendly; IDs masked)
 *   details(entry, family) → array of { label, value } pairs for the
 *     expanded body (full values shown)
 */
const VIEWS = {
  photo_id: {
    lines(e, family) {
      const numLine = e.id_number
        ? `№ ${maskNumber(e.id_number)}${
            e.date_of_expiry ? " · Expires " + e.date_of_expiry : ""
          }`
        : e.date_of_expiry
        ? `Expires ${e.date_of_expiry}`
        : null;
      return [
        `${idTypeLabel(e)} · ${resolveHolder(e, family)}`,
        e.holder_name ? `Holder: ${e.holder_name}` : null,
        numLine,
        e.nationality ? `Issued: ${e.nationality}` : null,
      ].filter(Boolean);
    },
    details(e, family) {
      return [
        { label: "Type", value: idTypeLabel(e) },
        { label: "Holder", value: e.holder_name || resolveHolder(e, family) },
        { label: "ID number", value: e.id_number },
        { label: "Nationality", value: e.nationality },
        { label: "Issuing authority", value: e.issuing_authority },
        { label: "Date of birth", value: e.date_of_birth },
        { label: "Date of expiry", value: e.date_of_expiry },
      ];
    },
  },
  flight: {
    lines: (e) => [
      `${e.airline || "Flight"} ${e.flight_no || ""}`,
      `${e.date || "—"} · ${e.from || "—"} → ${e.to || "—"}`,
      e.confirmation ? `Conf: ${e.confirmation}` : null,
    ].filter(Boolean),
    details: (e) => [
      { label: "Airline", value: e.airline },
      { label: "Flight #", value: e.flight_no },
      { label: "Date", value: e.date },
      { label: "From", value: e.from },
      { label: "To", value: e.to },
      { label: "Confirmation", value: e.confirmation },
    ],
  },
  insurance: {
    lines(e, family) {
      return [
        `${e.provider || "Travel insurance"} · ${resolveHolder(e, family)}`,
        e.policy_no ? `Policy ${maskNumber(e.policy_no)}` : null,
        e.coverage_start || e.coverage_end
          ? `${e.coverage_start || "—"} → ${e.coverage_end || "—"}`
          : null,
      ].filter(Boolean);
    },
    details(e, family) {
      return [
        { label: "Insured", value: e.holder_name || resolveHolder(e, family) },
        { label: "Provider", value: e.provider },
        { label: "Policy #", value: e.policy_no },
        { label: "Plan type", value: e.plan_type },
        { label: "Coverage starts", value: e.coverage_start },
        { label: "Coverage ends", value: e.coverage_end },
        { label: "24/7 emergency #", value: e.emergency_phone },
      ];
    },
  },
  hotel: {
    lines: (e) => [
      `${e.name || "Hotel"}`,
      `${e.checkin || "—"} → ${e.checkout || "—"}`,
      e.confirmation ? `Conf: ${e.confirmation}` : null,
    ].filter(Boolean),
    details: (e) => [
      { label: "Hotel", value: e.name },
      { label: "Check-in", value: e.checkin },
      { label: "Check-out", value: e.checkout },
      { label: "Confirmation", value: e.confirmation },
    ],
  },
  family: {
    lines: (e) => [`${e.name || "Family member"}`, e.relation || null].filter(Boolean),
    details: (e) => [
      { label: "Name", value: e.name },
      { label: "Relation", value: e.relation },
    ],
  },
  car: {
    lines: (e) => [
      `${e.company || "Car rental"}${e.vehicle ? " · " + e.vehicle : ""}`,
      `${e.pickup_date || "—"} → ${e.dropoff_date || "—"}`,
      e.pickup_location ? `Pickup: ${e.pickup_location}` : null,
      e.confirmation ? `Conf: ${e.confirmation}` : null,
    ].filter(Boolean),
    details: (e) => [
      { label: "Company", value: e.company },
      { label: "Vehicle class", value: e.vehicle },
      { label: "Pickup location", value: e.pickup_location },
      { label: "Pickup date", value: e.pickup_date },
      { label: "Dropoff date", value: e.dropoff_date },
      { label: "Confirmation", value: e.confirmation },
    ],
  },
  lounge: {
    lines: (e) => [
      `${e.program || "Lounge program"}${e.tier ? " · " + e.tier : ""}`,
      e.member_no ? `№ ${maskNumber(e.member_no)}` : null,
      e.expires ? `Expires ${e.expires}` : null,
    ].filter(Boolean),
    details: (e) => [
      { label: "Program", value: e.program },
      { label: "Member #", value: e.member_no },
      { label: "Tier", value: e.tier },
      { label: "Expires", value: e.expires },
    ],
  },
  tsaprecheck: {
    lines: (e) => [
      `TSA PreCheck® · ${e.holder_name || "Me"}`,
      e.ktn ? `KTN ${maskNumber(e.ktn)}` : null,
      e.expires ? `Expires ${e.expires}` : null,
    ].filter(Boolean),
    details: (e) => [
      { label: "Traveler", value: e.holder_name },
      { label: "KTN", value: e.ktn },
      { label: "Expires", value: e.expires },
    ],
  },
  globalentry: {
    lines: (e) => [
      `Global Entry · ${e.holder_name || "Me"}`,
      e.pass_id ? `PASS ${maskNumber(e.pass_id)}` : null,
      e.expires ? `Expires ${e.expires}` : null,
    ].filter(Boolean),
    details: (e) => [
      { label: "Traveler", value: e.holder_name },
      { label: "PASS ID", value: e.pass_id },
      { label: "Expires", value: e.expires },
    ],
  },
};

export function renderEntryLines(catId, entry, family) {
  return VIEWS[catId]?.lines(entry, family) || [JSON.stringify(entry)];
}

/** Returns only fields with a non-empty trimmed value. */
export function renderEntryDetails(catId, entry, family) {
  const rows = VIEWS[catId]?.details(entry, family) || [];
  return rows.filter(
    (r) => r.value !== undefined && r.value !== null && String(r.value).trim() !== ""
  );
}
